import { PureComponent } from "react";
import { withGenericConfig } from "/contexts/generic-config-context";
import { getContactFormConfigById } from "client/services/hubspot-service/contact-form-config";
import { sendHubspotForm, generateHiddenData } from "client/services/hubspot-service/hubspot-service";
import {
  HUBSPOT_FORM_SUBMIT_CLICKED,
  HUBSPOT_FORM_SUBMIT_FAILED,
  HUBSPOT_FORM_READY,
  HUBSPOT_FORM_INPUT_BLUR,
  CONTACT_SALES_FORM_SUBMISSION_FAILED
} from "constants/bigbrain-event-types";
import DataLayerEventTypes from "constants/data-layer-event-types";
import { fireGtmEvent } from "client/services/gtm-service/gtm-service";
import { IPO_CAMPAIGN_ID, GARTNER_CAMPAIGN_ID } from "constants/sales/campaigns";
import BaseForm from "segments/desktop/core-components/base-form/base-form";
import { withAbTests } from "contexts/ab-tests-context";
import { trackEvent } from "services/bigbrain-service";
import { handleClientException } from "client/services/exceptions-handling-service/exceptions-handling-service";
import { enterV13TestIfNeeded } from "services/ab-tests/monetization-ab-tests-service";
import { inputTypes } from "client/services/form-service/form-inputs";
import { getInputDataByName } from "client/services/form-service/form-service";
import { HUBSPOT_API_COMPANY_SIZE } from "client/services/hubspot-service/hubspot-constants";
import DefaultProps from "./default-props";
import { HUBSPOT_TO_LEAD_DATA_MAP } from "./consts";
import { componentStyles } from "./hubspot-contact-form.scss";

const HUBSPOT_INVALID_EMAIL_ERROR_TYPE = "INVALID_EMAIL";
const GARTNER_REPORT_URL = "https://www.gartner.com/doc/reprints?id=1-2AKZAKIR&ct=220714&st=sb";

class HubspotContactForm extends PureComponent {
  componentDidMount() {
    const { formId } = this.props;
    trackEvent(HUBSPOT_FORM_READY, { kind: formId });
  }

  getFirstErrorMessageFromResponse = (response) => {
    if (response?.errors?.[0]?.errorType === HUBSPOT_INVALID_EMAIL_ERROR_TYPE) {
      // Formatting Hubspot message into more user friendly one for known message types
      return "form.errors.invalidEmailError";
    }

    return response?.errors?.[0]?.message;
  };

  convertValuesToHubspotFormat = (formData) => {
    const { withPrivacyNotice, formId, overrideCampaignId, formTitle, lastNameFirst } = this.props;
    const formConfig = getContactFormConfigById({
      formId,
      overrideCampaignId,
      withPrivacyNotice,
      formTitle,
      lastNameFirst
    });
    const convertedData = [];
    formData.forEach((inputField) => {
      const inputConfig = getInputDataByName(formConfig, inputField.name);
      if (inputConfig.type === inputTypes.MULTI_SELECT) {
        if (Array.isArray(inputField.value)) {
          convertedData.push({
            name: inputField.name,
            value: inputField.value.map((option) => option.value).join(";")
          });
        } else {
          convertedData.push({
            name: inputField.name,
            value: inputField.value.value
          });
        }
      } else {
        convertedData.push(inputField);
      }
    });
    return convertedData;
  };

  convertToLeadData = (convertedFormData) => {
    const leadData = {};
    Object.keys(HUBSPOT_TO_LEAD_DATA_MAP).forEach((key) => {
      const hubspotKey = HUBSPOT_TO_LEAD_DATA_MAP[key];
      const field = convertedFormData.find((item) => item.name === hubspotKey);
      if (field) {
        leadData[key] = field.value;
      }
    });
    return leadData;
  };

  sendFormToHubspot = async (formData) => {
    try {
      const {
        formId,
        portalId,
        abTests,
        cookies,
        isV13PricingVersion,
        withPrivacyNotice,
        source,
        overrideCampaignId,
        cloudfrontViewerCountry
      } = this.props;

      const hiddenDataParams = generateHiddenData({
        withPrivacyNotice,
        source,
        overrideCampaignId,
        cookies,
        cloudfrontViewerCountry
      });
      const convertedData = this.convertValuesToHubspotFormat(formData);
      const fields = [...convertedData, ...hiddenDataParams];

      const companySizeValue = fields.find((item) => item.name === HUBSPOT_API_COMPANY_SIZE)?.value ?? null;

      const response = await sendHubspotForm(portalId, formId, fields);
      const success = response && !response.status; // Success request has no status entry
      let errorMessage = null;

      if (!success) {
        const firstErrorType = response?.errors && response?.errors[0]?.errorType;
        errorMessage = this.getFirstErrorMessageFromResponse(response);
        trackEvent(HUBSPOT_FORM_SUBMIT_FAILED, { kind: formId, info1: firstErrorType, info2: errorMessage });
      } else {
        trackEvent(HUBSPOT_FORM_SUBMIT_CLICKED, { kind: formId, info1: companySizeValue });
        fireGtmEvent(DataLayerEventTypes.HUBSPOT_FORM_SUBMITTED, {
          formId,
          lead_data: this.convertToLeadData(convertedData)
        });
      }

      enterV13TestIfNeeded(abTests, cookies, undefined, isV13PricingVersion);

      return { success, errorMessage };
    } catch (e) {
      await handleClientException({ e, bbEventParams: { name: CONTACT_SALES_FORM_SUBMISSION_FAILED } });
    }
  };

  getSubmitScreenData = () => {
    const { overrideCampaignId } = this.props;
    switch (overrideCampaignId) {
      case IPO_CAMPAIGN_ID:
        return {
          titleKey: "forms.thanksPage.ipoCampaign.title",
          subTitleHtmlKey: "forms.thanksPage.ipoCampaign.subTitleHtml"
        };
      case GARTNER_CAMPAIGN_ID:
        return {
          titleKey: "forms.thanksPage.gartnerCampaign.title",
          subTitleHtmlKey: "forms.thanksPage.gartnerCampaign.subTitleHtml2023",
          footerHtml: "forms.thanksPage.gartnerCampaign.footerHtml",
          downloadButtonLink: GARTNER_REPORT_URL
        };
      default:
        return null;
    }
  };

  onInputBlur = (name, value) => {
    const { formId } = this.props;
    trackEvent(HUBSPOT_FORM_INPUT_BLUR, { kind: formId, info1: name, info2: value?.length });
  };

  render() {
    const {
      withPrivacyNotice,
      formId,
      overrideCampaignId,
      backgroundLayers,
      isMobileOrTabletBrowser,
      formTitle,
      lastNameFirst,
      urlToRedirectAfterSubmission
    } = this.props;
    const formConfig = getContactFormConfigById({
      formId,
      overrideCampaignId,
      withPrivacyNotice,
      formTitle,
      lastNameFirst
    });
    const submitScreenData = this.getSubmitScreenData();

    return (
      <div className="hubspot-contact-form-wrapper">
        <BaseForm
          backgroundLayers={isMobileOrTabletBrowser ? 0 : backgroundLayers}
          formConfig={formConfig}
          submitFormAsyncCallback={this.sendFormToHubspot}
          submitScreenData={submitScreenData}
          onInputBlur={this.onInputBlur}
          renderPrivacyNotice={withPrivacyNotice}
          urlToRedirectAfterSubmission={urlToRedirectAfterSubmission}
        />
        <style jsx>{componentStyles}</style>
      </div>
    );
  }
}

HubspotContactForm.defaultProps = DefaultProps;

export default withAbTests(withGenericConfig(HubspotContactForm));
